import { FC, useCallback, useEffect, useState } from "react";
import { Button, ConfigProvider, message } from "antd";
// @ts-ignore
import { FormattedNumber } from "@umijs/max";
import classNames from "classnames";

import styles from "./index.less";

import { ReactComponent as WechatPayIcon } from "../../../../assets/svg/wechat-pay.svg";
import { ReactComponent as AlipayIcon } from "../../../../assets/svg/alipay.svg";

import {
  ServicePaymentAgreementUrl,
  ResponseCode,
} from "../../../../constants";
import {
  createOrderService,
  DisplayPayStatusEnum,
  IServiceOrderDto,
  OrderInfo,
  PayType,
} from "../../../../service";

import WaitingForAgreeTerms from "./components/WaitingForAgreeTerms";
import PayWithBalance from "./components/PayWithBalance";
import PayQRCode from "./components/PayQRCode";
import PayWithBankTransfer from "./components/PayWithBankTransfer";

import { PrimaryTheme } from "@packages/theme";

interface PayInformationProps {
  /** 余额 */
  balance?: number;
  /** 支付方式 */
  payMethod?: PayType;
  /** 支付金额 */
  payAmount?: number;
  /** 支付信息 */
  payInformation?: IServiceOrderDto;
  /** 支付成功回调 */
  onSuccess: (payId: string) => void;
  /** 确认转账 */
  onPayWithBankTransferConfirm: () => void;
  /** 订单信息 */
  orderInfo?: OrderInfo;
  /** 设置订单信息 */
  setOrderInfo: React.Dispatch<React.SetStateAction<OrderInfo | undefined>>;
}

/**
 * 付费信息
 * @returns
 */
const PayInformation: FC<PayInformationProps> = ({
  payMethod,
  balance,
  payAmount,
  payInformation,
  onSuccess,
  onPayWithBankTransferConfirm,
  orderInfo,
  setOrderInfo,
}) => {
  const [status, setStatus] = useState<DisplayPayStatusEnum>(
    DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS
  );
  const [qrCodeExpiredTime, setQRCodeExpiredTime] = useState<number>();
  const [countdownText, setCountdownText] = useState<string>("--");

  const getPayMethodsLabelAndIconMap = useCallback(
    (payMethod: PayType) => {
      switch (payMethod) {
        case PayType.WECHAT:
          return {
            label: "微信",
            icon: <WechatPayIcon />,
          };
        case PayType.ALIPAY:
          return {
            label: "支付宝",
            icon: <AlipayIcon />,
          };
        default:
          return null;
      }
    },
    [payMethod]
  );

  useEffect(() => {
    if (qrCodeExpiredTime) {
      // TODO 用 performance 优化为高精度的倒计时组件
      const interval = setInterval(() => {
        const now = Date.now();
        if (qrCodeExpiredTime <= now) {
          setStatus(DisplayPayStatusEnum.QR_CODE_EXPIRED);
          setCountdownText("--");
          return;
        }

        if (qrCodeExpiredTime - now > 60 * 1000) {
          setCountdownText(
            `${Math.floor((qrCodeExpiredTime - now) / 60000)}分${Math.floor(
              ((qrCodeExpiredTime - now) % 60000) / 1000
            )}秒`
          );
        }

        if (
          qrCodeExpiredTime - now < 60 * 1000 &&
          qrCodeExpiredTime - now > 1 * 1000
        ) {
          setCountdownText(`${Math.floor((qrCodeExpiredTime - now) / 1000)}秒`);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [qrCodeExpiredTime]);

  useEffect(() => {
    setStatus(DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS);
    setQRCodeExpiredTime(void 0);
    setCountdownText("--");
  }, [payAmount, payMethod]);

  if (!payMethod) {
    return null;
  }

  /**
   * 同意协议并创建订单
   */
  const handleAgreeTerms = async () => {
    if (!payInformation) {
      return;
    }

    if (!orderInfo) {
      // 创建订单号
      const rsp = await createOrderService(payInformation);

      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }

      setOrderInfo(rsp.data);
    }

    // 扫码支付的情况
    if ([PayType.WECHAT, PayType.ALIPAY].includes(payMethod)) {
      setStatus(DisplayPayStatusEnum.QR_CODE_LOADING);
    }

    // 银行卡转账和余额支付的情况
    if ([PayType.OFFLINE, PayType.BALANCE].includes(payMethod)) {
      setStatus(DisplayPayStatusEnum.PAYING);
    }
  };

  return (
    <ConfigProvider theme={PrimaryTheme}>
      <div className={styles.container}>
        {/* 带二维码的 */}
        {[PayType.WECHAT, PayType.ALIPAY].includes(payMethod) && (
          <div className={styles["with-qrcode"]}>
            <div className={styles["qrcode-box"]}>
              {/* 等待同意用户支付协议 */}
              {status === DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS && (
                <div
                  className={classNames(
                    styles["qrcode-box-common"],
                    styles["qrcode-box-agree"]
                  )}
                >
                  <div className={styles["agree"]}>
                    <div className={styles["text"]}>支付前请阅读</div>
                    <a
                      className={styles["link"]}
                      onClick={() => {
                        window.open(ServicePaymentAgreementUrl, "_blank");
                      }}
                    >
                      《服务支付协议》
                    </a>
                  </div>
                  <Button type="primary" onClick={handleAgreeTerms}>
                    同意并支付
                  </Button>
                </div>
              )}

              {status !== DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS && (
                <PayQRCode
                  payType={payMethod}
                  status={status}
                  updateStatus={setStatus}
                  orderInfo={orderInfo}
                  onSuccess={onSuccess}
                  setQRCodeExpiredTime={setQRCodeExpiredTime}
                />
              )}
            </div>

            <div className={styles["information-box"]}>
              <div className={styles["title"]}>实付金额</div>
              <div className={styles["price-box"]}>
                <div className={styles["price"]}>
                  {payAmount ? (
                    <FormattedNumber
                      value={payAmount}
                      style="currency"
                      currency="CNY"
                    />
                  ) : (
                    "--"
                  )}
                </div>
                <div className={styles["pay-channel"]}>
                  {getPayMethodsLabelAndIconMap(payMethod)?.icon}
                  请使用「{getPayMethodsLabelAndIconMap(payMethod)?.label}
                  」扫码支付
                </div>
                <div className={styles["qrcode-expired"]}>
                  二维码
                  <div className={styles["countdown"]}>{countdownText}</div>
                  后失效
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 银行转账的 */}
        {payMethod === PayType.OFFLINE && (
          <>
            {status === DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS && (
              <WaitingForAgreeTerms handleAgreeTerms={handleAgreeTerms} />
            )}
            {status === DisplayPayStatusEnum.PAYING && (
              <PayWithBankTransfer
                orderInfo={orderInfo}
                payAmount={payAmount}
                onPayWithBankTransferConfirm={onPayWithBankTransferConfirm}
              />
            )}
          </>
        )}

        {/* 余额支付 */}
        {payMethod === PayType.BALANCE && (
          <>
            {status === DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS && (
              <WaitingForAgreeTerms handleAgreeTerms={handleAgreeTerms} />
            )}

            {status === DisplayPayStatusEnum.PAYING && (
              <PayWithBalance
                orderInfo={orderInfo}
                payAmount={payAmount}
                balance={balance}
                onSuccess={onSuccess}
              />
            )}
          </>
        )}
      </div>
    </ConfigProvider>
  );
};

export default PayInformation;
